import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { RoleSettingsResponse } from '@/types/roleSettings';
import { FrontendSettings } from '@/types/userSettings';
import ajax from '@/utils/ajax';

const PATH = '/api/roles/settings/';

const EMPTY_RESPONSE: RoleSettingsResponse = {
  current_role: null,
  current_roles: [],
  can_edit: false,
  roles: []
};

export const roleSettingsRepository = {
  async getRoleSettings({ host }: { host: string }): Promise<RoleSettingsResponse> {
    const { data } = await ajax.get({ url: `${host}${PATH}` });

    return (data as RoleSettingsResponse) ?? EMPTY_RESPONSE;
  },

  /**
   * Sends only the changed keys for one role. The backend shallow-merges them,
   * and a null value clears that default.
   */
  async patchRoleSettings({
    host,
    role,
    settings
  }: {
    host: string;
    role: string;
    settings: FrontendSettings;
  }): Promise<FrontendSettings> {
    const { data } = await ajax.patch<{ role: string; frontend_settings: FrontendSettings }>({
      url: `${host}${PATH}`,
      data: { role, frontend_settings: settings }
    });

    return (data as unknown as { frontend_settings: FrontendSettings }).frontend_settings ?? {};
  }
};

const RoleSettingsRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async getRoleSettings(): Promise<RoleSettingsResponse> {
      return roleSettingsRepository.getRoleSettings({ host });
    },
    async patchRoleSettings(role: string, settings: FrontendSettings): Promise<FrontendSettings> {
      return roleSettingsRepository.patchRoleSettings({ host, role, settings });
    }
  };
};

export default RoleSettingsRepository;
